'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { unstable_isUnrecognizedActionError } from 'next/dist/client/components/unrecognized-action-error';

import { registerCustomer, updateCheckoutAddress } from '@/actions';
import { toast, useForm, zodResolver } from '@/libs';
import { searchAddressByCep } from '@/services';
import { useCheckoutStore, useCustomerStore } from '@/stores';
import { addressSchema, type Address } from '@/types/models';
import { formatErrorMessage } from '@/types/models/ApiError';
import { formatCEP, formatState, logger, parseDigits } from '@/utils';
import { clearCachedCustomerProfile } from '@/utils/customerProfileCache';

type UseAddressFormParams = {
  onContinue: (address: Address) => void;
  isLoading?: boolean;
  initialAddress?: Partial<Address>;
};

type UseAddressFormReturn = {
  control: ReturnType<typeof useForm<Address>>['control'];
  errors: ReturnType<typeof useForm<Address>>['formState']['errors'];
  isValid: boolean;
  isSubmitting: boolean;
  isSearchingCep: boolean;
  handleSubmit: (e: React.FormEvent) => void;
  submitAddressForm: () => void;
  handleCepBlur: () => Promise<void>;
  watchZipCode: string;
};

export const useAddressForm = ({
  onContinue,
  isLoading = false,
  initialAddress,
}: UseAddressFormParams): UseAddressFormReturn => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSearchingCep, setIsSearchingCep] = useState<boolean>(false);
  const [isCepVerified, setIsCepVerified] = useState<boolean>(false);
  const customerData = useCustomerStore((state) => state.customerData);
  const setCustomerData = useCustomerStore((state) => state.setCustomerData);
  const utmData = useCheckoutStore((state) => state.checkoutData.utmData);
  // const { setCustomerBalance } = useCheckoutStore();
  const lastSearchedCepRef = useRef<string>('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
  } = useForm<Address>({
    mode: 'onChange',
    resolver: zodResolver(addressSchema),
    defaultValues: {
      zipCode: initialAddress?.zipCode || '',
      street: initialAddress?.street || '',
      number: initialAddress?.number || '',
      neighborhood: initialAddress?.neighborhood || '',
      city: initialAddress?.city || '',
      state: initialAddress?.state || '',
      complement: initialAddress?.complement || '',
    },
  });

  const watchZipCode = watch('zipCode');

  useEffect(() => {
    if (initialAddress?.zipCode) {
      setIsCepVerified(true);
    }
  }, [initialAddress?.zipCode]);

  const searchAddress = useCallback(
    async (zipCode: string) => {
      if (!zipCode) return;

      const cleanCep = zipCode.replace(/\D/g, '');
      if (cleanCep.length !== 8) return;

      if (lastSearchedCepRef.current === cleanCep) return;

      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
        toastTimeoutRef.current = null;
      }

      setIsSearchingCep(true);
      lastSearchedCepRef.current = cleanCep;

      toastTimeoutRef.current = setTimeout(() => {
        toast.info('Buscando endereço...');
      }, 1000);

      try {
        const addressData = await searchAddressByCep(zipCode);

        if (toastTimeoutRef.current) {
          clearTimeout(toastTimeoutRef.current);
          toastTimeoutRef.current = null;
        }

        setValue('street', addressData.street, { shouldValidate: true });
        setValue('neighborhood', addressData.neighborhood, { shouldValidate: true });
        setValue('city', addressData.city, { shouldValidate: true });
        setValue('state', formatState(addressData.state), { shouldValidate: true });

        await trigger(['street', 'neighborhood', 'city', 'state']);

        setIsCepVerified(true);
        toast.success('Endereço encontrado!');
      } catch (error) {
        if (toastTimeoutRef.current) {
          clearTimeout(toastTimeoutRef.current);
          toastTimeoutRef.current = null;
        }

        lastSearchedCepRef.current = '';
        setIsCepVerified(false);
        logger.error('Erro ao buscar CEP', error);
        toast.error('Confirme o CEP antes de tentar novamente.');
      } finally {
        setIsSearchingCep(false);
      }
    },
    [setValue, trigger],
  );

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const cleanCep = watchZipCode.replace(/\D/g, '');

    if (cleanCep.length < 8 || lastSearchedCepRef.current !== cleanCep) {
      setIsCepVerified(false);
    }

    if (cleanCep.length === 8 && lastSearchedCepRef.current !== cleanCep) {
      searchTimeoutRef.current = setTimeout(() => {
        searchAddress(watchZipCode);
      }, 500);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [watchZipCode, searchAddress]);

  const handleCepBlur = useCallback(async () => {
    const currentZipCode = watchZipCode;
    if (!currentZipCode) return;

    const cleanCep = currentZipCode.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    if (lastSearchedCepRef.current === cleanCep) return;

    await searchAddress(currentZipCode);
  }, [watchZipCode, searchAddress]);

  const onSubmit = useCallback(
    async (data: Address) => {
      if (isLoading || isSubmitting) return;

      setIsSubmitting(true);

      const loadingToastId = toast.loading('Processando endereço...');

      try {
        let customerId = customerData?.customerId;
        const pendingRegistrationData = customerData?.pendingRegistrationData;

        // Se há dados de registro pendentes e não há customerId, registrar o usuário primeiro
        if (pendingRegistrationData && (!customerId || customerId.trim() === '')) {
          toast.dismiss(loadingToastId);
          const registeringToastId = toast.loading('Criando cadastro...');

          try {
            const { data: registeredCustomer, error } = await registerCustomer({
              name: pendingRegistrationData.fullname,
              socialName: pendingRegistrationData.socialName,
              cpf: pendingRegistrationData.document,
              email: pendingRegistrationData.email || '',
              phone: pendingRegistrationData.phone,
              utm: utmData || undefined,
            });

            if (error || !registeredCustomer) {
              throw new Error(
                error?.friendlyMessage || 'Confirme os dados e tente novamente',
              );
            }

            customerId = registeredCustomer.customerId;

            clearCachedCustomerProfile(registeredCustomer.customerId);

            // Obter saldo do cliente
            // const balance = await getCustomerBalance(customerId);
            // if (balance) {
            //   setCustomerBalance(balance);
            // }

            // Atualizar customerStore com o customerId e remover dados pendentes
            setCustomerData({
              document: pendingRegistrationData.document,
              email: pendingRegistrationData.email || '',
              phone: pendingRegistrationData.phone,
              name:
                pendingRegistrationData.socialName || pendingRegistrationData.fullname,
              hasAccount: true,
              hasMail: true,
              hasAddress: true,
              customerId: registeredCustomer.customerId,
            });

            toast.dismiss(registeringToastId);
            toast.success('Cadastro criado com sucesso!');
          } catch (error) {
            toast.dismiss(registeringToastId);
            logger.error('Erro ao registrar cliente:', error);

            if (unstable_isUnrecognizedActionError(error)) {
              toast.warning('Nova versão disponível', {
                description: 'Recarregue a página para continuar.',
                action: { label: 'Recarregar', onClick: () => location.reload() },
              });
              return;
            }

            const errorMessage =
              error instanceof Error && error.message
                ? error.message
                : 'Confirme os dados e tente novamente';
            toast.error(errorMessage);
            throw error;
          }
        }

        if (!customerId || customerId.trim() === '') {
          toast.dismiss(loadingToastId);
          toast.error(
            'Dados do cliente não encontrados. Por favor, complete o cadastro primeiro.',
          );
          logger.error('CustomerId não encontrado ao tentar salvar endereço', {
            customerData,
          });
          return;
        }

        const formattedAddress: Address = {
          ...data,
          zipCode: formatCEP(data.zipCode),
          state: formatState(data.state),
        };

        const cleanZipCode = parseDigits(data.zipCode);
        logger.info('Salvando endereço para customerId:', customerId);

        const addressResponse = await updateCheckoutAddress({
          customerId,
          street: data.street,
          number: data.number,
          neighborhood: data.neighborhood,
          city: data.city,
          state: formatState(data.state),
          zipCode: cleanZipCode,
          complement: data.complement,
        });

        if (!addressResponse) {
          throw new Error('Confirme os dados e tente novamente na API');
        }

        if (customerData) {
          setCustomerData({
            ...customerData,
            hasAddress: true,
          });
        }

        logger.info('Endereço salvo com sucesso para customerId:', customerId);
        toast.dismiss(loadingToastId);
        toast.success('Endereço salvo com sucesso!');
        onContinue(formattedAddress);
      } catch (error) {
        toast.dismiss(loadingToastId);
        logger.error('Confirme os dados e tente novamente:', {
          error,
          customerId: customerData?.customerId,
          addressData: data,
        });
        toast.error(formatErrorMessage(error, 'Confirme os dados e tente novamente'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [isLoading, isSubmitting, onContinue, customerData, setCustomerData],
  );

  const submitAddressForm = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return {
    control,
    errors,
    isValid: isValid && isCepVerified,
    isSubmitting,
    isSearchingCep,
    handleSubmit: (e: React.FormEvent) => {
      e.preventDefault();
      handleSubmit(onSubmit)(e);
    },
    submitAddressForm,
    handleCepBlur,
    watchZipCode,
  };
};
